import { ArrowUpRight, Sprout, Flower2, Trees, Heart } from "lucide-react";
import { PageHero, CTABand } from "../components/Shared";
import { Reveal } from "../components/Reveal";
import { GIVING_URL, IMAGES } from "../lib/constants";
import { useTitle } from "../lib/useTitle";

const TIERS = [
  { icon: Sprout, name: "Seedling", amount: "$25", impact: "Provides garden seeds, soil, and supplies for one woman's first planting bed." },
  { icon: Flower2, name: "Cultivator", amount: "$100", impact: "Covers a month of nourishing shared meals and cooking workshop ingredients." },
  { icon: Trees, name: "Sustainer", amount: "$500", impact: "Sponsors a season of holistic health coaching and vocational training materials." },
  { icon: Heart, name: "Custom Gift", amount: "Any", impact: "Every seed matters. Give what is on your heart — it all grows something." },
];

export default function Donate() {
  useTitle("Donate | Bee Courageous @ Garden Grub");
  return (
    <>
      <PageHero
        eyebrow="Donate"
        lines={["Sow a seed today.", "Watch a life bloom."]}
        lede="Your generosity plants gardens, sets tables, funds coaching, and walks women toward self-sufficiency. Giving is handled securely through our giving platform."
        image={IMAGES.peppers}
        imageAlt="Vibrant freshly harvested peppers and produce from the garden"
      />

      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-12 lg:py-28">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
          {TIERS.map((t, i) => (
            <Reveal key={t.name} delay={0.08 * i}>
              <a
                href={GIVING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="card-soft group flex h-full flex-col !border-pollen/30 hover:!border-pollen"
                data-testid={`donate-tier-${t.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <span className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-pollen-soft text-pollen-dark transition-colors duration-300 group-hover:bg-pollen group-hover:text-white">
                  <t.icon className="h-6 w-6" aria-hidden="true" />
                </span>
                <p className="font-serif text-3xl font-semibold text-charcoal">{t.amount}</p>
                <h3 className="mt-1 text-xs font-bold uppercase tracking-[0.2em] text-wing">{t.name}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-charcoal/70">{t.impact}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-garden transition-transform duration-300 group-hover:translate-x-1">
                  Give this gift <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
                </span>
              </a>
            </Reveal>
          ))}
        </div>

        <Reveal className="mx-auto mt-14 max-w-3xl text-center">
          <p className="text-sm leading-relaxed text-charcoal/60">
            Bee Courageous @ Garden Grub is a faith-based nonprofit healing program. Give securely through our giving
            platform using any of the options above. To give by mail, sponsor an event, or discuss a partnership gift,
            please <a href="/contact" className="font-bold text-wing underline">contact us</a>.
          </p>
        </Reveal>
      </section>

      <section className="bg-garden-soft/60 py-20 lg:py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-12 lg:px-12">
          <Reveal className="lg:col-span-7">
            <h2 className="font-serif text-3xl font-semibold text-charcoal sm:text-4xl">Where your gift goes</h2>
            <ul className="mt-6 space-y-4">
              {[
                "Nourishing meals and cooking education for women and their children",
                "Seeds, tools, and beds for the community garden that feeds the program",
                "Holistic health coaching and trauma-informed support circles",
                "Vocational training, certifications, and interview-ready resources",
                "Emergency stabilization support for women in crisis transition",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-charcoal/80 sm:text-base" data-testid={`impact-${i}`}>
                  <span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-pollen" aria-hidden="true" />
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="arch-frame overflow-hidden border-4 border-white shadow-2xl shadow-garden/15">
              <img src={IMAGES.gardener} alt="A program participant harvesting vegetables with a smile" className="aspect-[4/5] w-full object-cover" loading="lazy" />
            </div>
          </Reveal>
        </div>
      </section>

      <CTABand
        title="Your generosity becomes her testimony."
        text="Every gift — seedling-sized or sustaining — helps a woman write a new story."
        primaryLabel="Other Ways to Help"
      />
    </>
  );
}
